import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from 'react';
import { hasAuthParams, useAuth } from 'react-oidc-context';
import { AuthConf, isHs256UnsafeAuthConfig, isRs256AuthConfig } from '../config/config';
import { decodeToken, generateToken } from '../utils/auth';

export type UserState = {
  loggedInUser?: LoggedInUser;
  login: (id: string) => void;
  logout: () => void;
  isSelfSignedToken: () => boolean;
  signoutOnUnauthorized: (err: unknown) => void | undefined;
};

export type LoggedInUser = {
  userId: string;
  userAccessToken: string;
};
export declare class ApiException<T> extends Error {
  code: number;

  body: T;

  headers: {
    [key: string]: string;
  };
  constructor(
    code: number,
    message: string,
    body: T,
    headers: {
      [key: string]: string;
    },
  );
}

export const UserContext = React.createContext<UserState | undefined>(undefined);

export const useUserState: () => UserState = () => {
  const user = useContext<UserState | undefined>(UserContext);
  if (!user) {
    throw new Error('User context not initialized');
  }
  return user;
};

export const useLoggedInUser: () => LoggedInUser = () => {
  const userState = useUserState();
  if (userState.loggedInUser) {
    return userState.loggedInUser;
  }
  throw new Error('User not logged in, check if you guarded this by authcheck');
};

export const UserProvider = ({
  auth,
  children,
}: PropsWithChildren<{
  auth: AuthConf;
}>) => {
  const [loggedInUser, setLoggedInUser] = useState<LoggedInUser>();
  const oidcAuth = useAuth();

  useEffect(() => {
    if (isRs256AuthConfig(auth) && oidcAuth && oidcAuth.isAuthenticated && oidcAuth.user) {
      const userId = decodeToken(oidcAuth.user.access_token).sub!;
      setLoggedInUser({
        userId,
        userAccessToken: oidcAuth.user.access_token,
      });
    }
  }, [auth, oidcAuth]);

  const login = useCallback(
    async (userId: string) => {
      if (isHs256UnsafeAuthConfig(auth)) {
        const userAccessToken = await generateToken(
          userId,
          auth.secret,
          auth.audience,
          'daml_ledger_api',
        );
        setLoggedInUser({ userId, userAccessToken });
      } else if (
        !hasAuthParams() &&
        !oidcAuth.isAuthenticated &&
        !oidcAuth.activeNavigator &&
        !oidcAuth.isLoading
      ) {
        console.debug('User is unauthenticated. Signing in.');
        await oidcAuth.signinRedirect();
      } else {
        console.debug('Authentication process is still executing.');
      }
    },
    [auth, oidcAuth],
  );

  const logout = useCallback(async () => {
    setLoggedInUser(undefined);

    if (isRs256AuthConfig(auth) && oidcAuth) {
      const idTokenHint = oidcAuth.user?.id_token;
      await oidcAuth.removeUser();
      await oidcAuth.signoutRedirect({
        id_token_hint: idTokenHint,
        post_logout_redirect_uri: window.location.origin,
      });
    }
  }, [auth, oidcAuth]);

  const isApiException = (error: unknown): error is ApiException<unknown> => {
    return error !== null && typeof error === 'object' && 'code' in error;
  };

  const signoutOnUnauthorized = useCallback(
    (err: unknown) => {
      if (isApiException(err) && err.code === 401) {
        logout().catch(() => console.debug('There was an issue redirecting the user to logout.'));
      }
    },
    [logout],
  );

  const isSelfSignedToken = useCallback(() => isHs256UnsafeAuthConfig(auth), [auth]);

  const userState: UserState = useMemo(
    () => ({ loggedInUser, login, logout, isSelfSignedToken, signoutOnUnauthorized }),
    [loggedInUser, login, logout, isSelfSignedToken, signoutOnUnauthorized],
  );

  return <UserContext.Provider value={userState}>{children}</UserContext.Provider>;
};
